import { FacebookClient } from '@/infrastructure/clients/facebook/facebook-client';
import { GoogleClient } from '@/infrastructure/clients/google/google-client';
import { LinkedInClient } from '@/infrastructure/clients/linkedin/linkedin-client';
import { SignInFlow } from '@/infrastructure/clients/studentapi/enums/sign-in-flow';
import { IStudentSignResult } from '@/infrastructure/clients/studentapi/interfaces/student-sign-result.interface';
import { StudentApiClient } from '@/infrastructure/clients/studentapi/student-api-client';
import { AuthenticationProviderType } from '../enums/authentication-provider-type';
import { EventType } from '../enums/event-type';
import { ProviderCustomActionType } from '../enums/provider-custom-action-type';
import { DirectProvider } from '../providers/direct-provider';
import { FacebookProvider } from '../providers/facebook-provider';
import { GoogleProvider } from '../providers/google-provider';
import { LinkedInProvider } from '../providers/linkedin-provider';
import { EventBroadcastService } from './event-broadcast-service';
import { DataStorage } from '@studyportals/data-storage';
import IEmailPreference from '../dto/email-preference.interface';
import { InternalEventType } from '../enums/internal-event-type';
import { SessionCreatedSubscriber } from '../subscribers/session-created-subscriber';
import { ITokenBasedSession } from '@studyportals/student-interfaces';
import globalStore from '@/store/global';
import globals from '@/utils/globals';
import { PortalType, Referrer } from 'interfaces';
import { GoogleButtonSize } from '../enums/google-button-size';
import { IAuthenticationState } from '@/domain/dto/student-authentication-state.interface';
import { IIncentiveStudentField } from '../dto/incentive-studentfield.interface';
import { buildLegacyRegistrationReferrer } from '@/utils/tracking/incentive-tracking';

declare const STUDENTAPI_URL: string;
declare const GOOGLE_CLIENT_ID: string;
declare const FACEBOOK_APP_ID: string;
declare const LINKEDIN_REDIRECT_URI: string;
declare const LINKEDIN_CLIENT_ID: string;

export class StudentAuthenticationService {
	private googleProvider: GoogleProvider;
	private facebookProvider: FacebookProvider;
	private linkedinProvider: LinkedInProvider;
	private directProvider: DirectProvider;

	constructor() {
		const studentAPIClient = new StudentApiClient(STUDENTAPI_URL);
		const googleClient = new GoogleClient(GOOGLE_CLIENT_ID);
		const facebookClient = new FacebookClient(FACEBOOK_APP_ID);
		const linkedInClient = new LinkedInClient(LINKEDIN_CLIENT_ID, LINKEDIN_REDIRECT_URI);

		this.googleProvider = new GoogleProvider(googleClient, studentAPIClient);
		this.facebookProvider = new FacebookProvider(facebookClient, studentAPIClient);
		this.linkedinProvider = new LinkedInProvider(linkedInClient, studentAPIClient);
		this.directProvider = new DirectProvider(studentAPIClient);
	}

	public async authenticate(
		state: IAuthenticationState,
		provider: AuthenticationProviderType,
		directAuthType?: ProviderCustomActionType,
		incentiveStudentFields?: IIncentiveStudentField,
	): Promise<IStudentSignResult> {
		const studentSignResult: IStudentSignResult = incentiveStudentFields
			? await this.authenticateWithProvider(state, provider, directAuthType, incentiveStudentFields)
			: await this.authenticateWithProvider(state, provider, directAuthType);

		this.broadcastEvents(studentSignResult);
		return studentSignResult;
	}

	public async forgotPassword(state: IAuthenticationState): Promise<void> {
		await this.directProvider.forgotPassword(
			state.email?.toLowerCase() || '',
			state.portalType || PortalType.MASTERS,
		);
	}

	public async confirmForgotPassword(email: string | null, password: string | null, resetToken: string | null): Promise<void> {
		const username = email || '';
		const code = resetToken || '';
		password = password || '';

		await this.directProvider.confirmForgotPassword(username, password, code);
	}

	public async changePassword(oldPassword: string | null, password: string | null): Promise<void> {
		password = password || '';
		const session = await globals.sessionService.getSession();

		if (session) {
			const email = session.getUser().email;
			oldPassword = oldPassword || '';
			await this.directProvider.changePassword(oldPassword, password, email);
		}
	}

	public async subscribeForEmails(state: IAuthenticationState, preferences?: IEmailPreference): Promise<void> {
		const accessToken = state.accessToken || await this.getCurrentAccessToken();
		if (preferences === undefined) {
			await this.directProvider.updateData(accessToken, {
				emailing_other: true,
				emailing_favourites: true,
				emailing_scholarships: true,
				emailing_updates: true,
			});
			return;
		}

		await this.directProvider.updateData(accessToken, preferences);
	}

	private async getCurrentAccessToken(): Promise<string> {
		const session = await globals.sessionService.getSession() as ITokenBasedSession | null;
		return session === null ? '' : session.getAccessToken();
	}

	public async showYolo(onCredential: (credential: string) => Promise<void>): Promise<void> {
		globals.eventBus.on(InternalEventType.YOLO_DISMISSED, () => {
			EventBroadcastService.broadcast(EventType.YOLO_POPUP_DISMISSED, null);
		});

		const yoloResult = await this.googleProvider.showYolo();

		if (yoloResult.status !== 'credential') return;

		await onCredential(yoloResult.credential);
	}

	public renderGoogleButton(options: { element: any; template: string, gButtonSize?: GoogleButtonSize }, referrer: Referrer | null): void {
		this.googleProvider.renderGoogleButton(options, referrer);
	}

	private broadcastEvents(studentSignResult: IStudentSignResult): void {
		const credentials = studentSignResult.credentials;

		if (studentSignResult.flow === SignInFlow.REGISTERED) {
			EventBroadcastService.broadcast(EventType.STUDENT_REGISTERED, credentials);
		} else {
			EventBroadcastService.broadcast(EventType.EXISTING_STUDENT_LOGGED_IN, credentials);
			// tslint:disable-next-line: no-unused-expression
			new SessionCreatedSubscriber(window['EventAggregationService']);
		}

		EventBroadcastService.broadcast(EventType.STUDENT_LOGGED_IN, credentials);
	}

	private authenticateWithProvider(
		state: IAuthenticationState,
		provider: AuthenticationProviderType,
		providerCustomActionType?: ProviderCustomActionType,
		incentiveStudentFields?: IIncentiveStudentField,
	): Promise<IStudentSignResult> {
		const payload = this.getPayload(state, incentiveStudentFields);

		switch (provider) {
		case AuthenticationProviderType.FACEBOOK:
			return this.authenticateFacebook(payload, providerCustomActionType!, state);
		case AuthenticationProviderType.LINKEDIN:
			return this.linkedinProvider.login(payload as object);
		case AuthenticationProviderType.GOOGLE:
			return this.authenticateGoogle(payload, state);
		case AuthenticationProviderType.DIRECT:
			return this.authenticateDirect(state, payload, providerCustomActionType!);
		default:
			throw new Error('AuthenticationProviderType not supported');
		}
	}

	// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
	private authenticateFacebook(payload: any, providerCustomAction: ProviderCustomActionType, state: IAuthenticationState) {
		if (providerCustomAction === ProviderCustomActionType.YOLO) {
			return this.facebookProvider.login(payload as object, state.facebookAccessToken ?? null);
		} else {
			return this.facebookProvider.login(payload as object, state.facebookAccessToken ?? null);
		}
	}

	private authenticateGoogle(payload: any, state: IAuthenticationState): Promise<IStudentSignResult> {
		return this.googleProvider.login(payload as object, state.googleIdToken ?? '');
	}

	private async authenticateDirect(
		state: IAuthenticationState,
		payload: any,
		providerCustomAction: ProviderCustomActionType,
	): Promise<IStudentSignResult> {
		const email = state.email || '';
		const password = state.password || '';

		if (providerCustomAction === ProviderCustomActionType.LOGIN) {
			return this.directProvider.login(email, password, payload as object);
		}

		if (providerCustomAction === ProviderCustomActionType.REGISTER) {
			payload = this.addNameToPayload(payload as object, state);
			const captchaToken = state.captcha;
			return this.directProvider.register(email, password, payload as object, captchaToken!);
		}

		throw new Error('DirectAuthenticationType not supported');
	}

	private getPayload(state: IAuthenticationState, incentiveStudentFields?: IIncentiveStudentField): any {
		const legacyReferrer = buildLegacyRegistrationReferrer(
			incentiveStudentFields?.registration_incentive,
			incentiveStudentFields?.registration_incentive_location,
			incentiveStudentFields?.registration_incentive_identifier,
		);

		let payload = {
			referrer: state.referrer,
			registration_portal_type: state.portalType,
			...(incentiveStudentFields ? {
				referrer: legacyReferrer,
				...incentiveStudentFields,
			} : {})
		};

		payload = this.addTrafficSourceToPayload(payload, state);

		if (undefined !== window['formEntity'] && null !== window['formEntity']['postValues']) {
			payload = Object.assign(window['formEntity']['postValues'], payload);
		}

		return payload;
	}

	private addNameToPayload(payload: any, state: IAuthenticationState): any {
		const name = state.name || '';

		const nameSplit = name.split(' ');
		payload.first_name = nameSplit.shift() || '';
		payload.last_name = nameSplit.join(' ');
		payload.name = name;
		return payload;
	}

	private addTrafficSourceToPayload(payload: any, state: IAuthenticationState): any {
		DataStorage.init();
		const trafficSourceData = DataStorage.retrieve('traffic_source');

		if (trafficSourceData?.['utm_source']) {
			if (trafficSourceData['utm_source'].source) {
				const trafficSource = trafficSourceData['utm_source'].source as string;
				payload['traffic_source'] = trafficSource;

				if (!state.skipGlobalStoreWrite) {
					globalStore.actions.saveTrafficSource(trafficSource);
				}
			}

			if (trafficSourceData['utm_source'].url) {
				payload['traffic_source_url'] = trafficSourceData['utm_source'].url as string;
			}
		}

		return payload;
	}
}
